import { Injectable, EventEmitter } from '@angular/core'
import { Router, NavigationStart } from '@angular/router'
import { Uri } from '@mprep/shared/core/models'
import { CommonAppStateKeys } from '@mprep/shared/core/services'
import { ResourceService } from '@mprep/shared/data/services'
import { DomUtils } from '@mprep/shared/lib/helpers'

import { PathNode, Task, Resource, ResourceType } from '@mprep/shared/data/models'

import { Utils } from '../../shared/'
import { AppStateService } from '../../shared/services/app-state'

import { WORKSPACE_BASE_URL, EXAMS_RETURN_URL } from '../'
import { ResourceSecureUriService } from '@mprep/shared/data/services'

const WORKSPACE_BASE_URL_REGEXP = /\/path\/\d+\/unit|\/path$/
const SWITCH_RESOURCE_URL_REGEXP = /resource\/([0-9,]+)/

type CurrectPosition = {
  currentPath: PathNode,
  currentUnit: PathNode,
  currentSection: PathNode,
  currentTask: Task
}

@Injectable()
export class NavigationService {

  public menuStatusChange: EventEmitter<any> = new EventEmitter<any>()
  private _lastWorkspaceUrl: string
  private _toggleSibebarEmitter: EventEmitter<any> = new EventEmitter<any>()
  private _menuStatus: boolean = false

  public get onToggleSidebar() {
    return this._toggleSibebarEmitter
  }

  constructor(
    private _router: Router,
    private _domUtils: DomUtils,
    private _appStateService: AppStateService,
    private _resourceSecureUriService: ResourceSecureUriService,
    private _resourceService: ResourceService
  ) {
    this._lastWorkspaceUrl = WORKSPACE_BASE_URL
    this._router.events
      .filter((event) => event instanceof NavigationStart)
      .subscribe((event: NavigationStart) => {
        if (event.url.match(WORKSPACE_BASE_URL_REGEXP)) {
          this._lastWorkspaceUrl = event.url
        }
      })
  }

  public navigateToResource(
    resource: Resource,
    currentPathPosition?: CurrectPosition
  ) {
    switch (resource.resourceType) {
      case ResourceType.Pdf:
        Utils.downloadUrl(resource.uri)
        break
      case ResourceType.Ebook:
        this._resourceSecureUriService.findModelById(resource.id)
          .subscribe((resourceSecureUri) => {
            Utils.downloadUrl(resourceSecureUri.uri)
          })
        break
      case ResourceType.BlogArticle:
      case ResourceType.Website:
        Utils.openInWindow(resource.uri)
        break
      case ResourceType.LsacPreptest:
        Utils.openInNewWindow(resource.uri)
        break
      case ResourceType.Navigator:
        this._routeToResource(true, currentPathPosition, resource)
        break
      case ResourceType.PrintBook:
      case ResourceType.Unknown:
        break
      case ResourceType.Exam:
      case ResourceType.OgExam:
        this.navigateToExam(resource, EXAMS_RETURN_URL)
        break
      case ResourceType.Atlas:
        this._router.navigate([resource.uri])
        break
      case ResourceType.Video:
      case ResourceType.Brightcove:
        this._router.navigate(['videos', resource.id])
        break
      case ResourceType.Placeholder:
        this._router.navigate(['placeholder'])
        break
      case ResourceType.Switch:
      case ResourceType.Cat:
      case ResourceType.Interact:
      case ResourceType.StudentCenter:
      case ResourceType.QuestionBank:
      default:
        this._routeToResource(false, currentPathPosition, resource)
        break
    }
  }

  public navigateToLastWorkspaceLocation() {
    this._router.navigateByUrl(this._lastWorkspaceUrl)
  }

  public navigateToWorkspace() {
    this._router.navigate(['/path'])
  }

  public navigateToDashboard() {
    this._router.navigate(['/dashboard'])
  }

  public navigateToNoSyllabusPage() {
    this._router.navigate(['/unauthorized'], { skipLocationChange: true })
  }

  public navigateToUrl(url: string) {
    this._domUtils.window.location.href = url
  }

  public toggleSidebar() {
    this._toggleSibebarEmitter.emit()
  }

  public setMenuStatus(menuStatus: boolean) {
    this._menuStatus = menuStatus
    this.menuStatusChange.emit({ collapsed: this._menuStatus })
  }

  public navigateToExam(resource: Resource, overrideReturnUrl: string = '') {
    // TODO [GitHub Issue]: we should ensure that only exam resources can be used in here
    // https://github.com/ManhattanPrep/lms/issues/368
    const resourceUri = new Uri(resource.uri)
    const currentUrl = this._domUtils.window.location.href
    resourceUri.addQueryStringPart(CommonAppStateKeys.RETURN_URL, overrideReturnUrl || currentUrl)
    this.navigateToUrl(resourceUri.toString())
  }

  public navigateToSwitchSyllabus() {
    this._router.navigate([WORKSPACE_BASE_URL, this._appStateService.path.id, 'switch'])
  }

  private _routeToResource(showTask: boolean, currentPathPosition: CurrectPosition, resource: Resource) {
    let queryParams = {}
    const resourceSwitchMatch = resource.uri.match(SWITCH_RESOURCE_URL_REGEXP)

    if (resourceSwitchMatch) {
      const possibleIds = resourceSwitchMatch[1].split(',')
      this._resourceService.findModelsBy({ id: possibleIds }).subscribe((resources: Resource[]) => {
        this.navigateToResource(this._sortResourcesByIdOrder(resources, possibleIds)[0], currentPathPosition)
      })
    } else {
      if (typeof currentPathPosition === 'object') {
        queryParams['currentTask'] = Utils.propertyPresent(currentPathPosition, 'currentPath', 'id') ? `${currentPathPosition.currentPath.id}` : ''
        queryParams['currentTask'] += Utils.propertyPresent(currentPathPosition, 'currentUnit', 'id') ? `/${currentPathPosition.currentUnit.id}` : '/'
        queryParams['currentTask'] += Utils.propertyPresent(currentPathPosition, 'currentSection', 'id') ? `/${currentPathPosition.currentSection.id}` : '/'
        queryParams['currentTask'] += Utils.propertyPresent(currentPathPosition, 'currentTask', 'id') ? `/${currentPathPosition.currentTask.id}` : '/'
      }

      if (showTask && queryParams['currentTask']) {
        queryParams['showTask'] = true
      }


      this._router.navigate(['/resource/', resource.id], { queryParams: queryParams })
    }
  }

  private _sortResourcesByIdOrder(resources: Resource[], possibleIds: string[]): Resource[] {
    const idIndexMap = possibleIds.reduce((map, id, i) => map.set(String(id), i + 1), new Map())
    return resources.sort((firstResource: Resource, secondResource: Resource) => {
      return idIndexMap.get(String(firstResource.id)) - idIndexMap.get(String(secondResource.id))
    })
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/navigation.service.ts